/**
 * Analytics Plugin (client)
 * Initializes analytics providers and auto-tracking listeners.
 * v-track directives are registered in analytics-directives.ts (SSR + client).
 *
 * This file is a composition root (wiring only). It cannot be imported under the Nuxt Vitest
 * transform because it depends on the nuxt-gtag `useGtag` auto-import (`#imports`), which is not
 * registered in the test build. Its extractable logic is unit-tested elsewhere: the auth gate in
 * utils/auth-gate.spec.ts, the page-view dispatch in tracking/page-view-pipeline.spec.ts, the
 * backend event mapping in providers/backend-analytics.provider.spec.ts, and identify()/setContext
 * in services/analytics.service.spec.ts.
 */

import { watch } from 'vue'
import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { useLocationStore } from '@common/location-layer/store/location.store'
import { useLogger } from '@common/core-layer/composables/useLogger'
import { getConfig } from '@common/core-layer/config'
import { APP_NAMES } from '@common/core-layer/stores/global.store'
import { AnalyticsEvents } from '../types'
import { BackendAnalyticsProvider } from '../providers/backend-analytics.provider'
import { PendoAnalyticsProvider } from '../providers/pendo-analytics.provider'
import { analyticsService } from '../services/analytics.service'
import { registerDefaultIdentitySource } from '../tracking/analytics-context'
import { canSendAuthenticatedAnalytics as computeAuthGate } from '../utils/auth-gate'
import { getAutoTrackedClick } from '../directives/analytics.directives'
import { startPageViewTracking } from '../tracking/page-view-pipeline'
import { registerGoogleAnalyticsFromPlugin } from '../utils/register-google-analytics-from-plugin.utils'
import { isGoogleAnalyticsSendEnabled } from '../utils/is-google-analytics-send-enabled.utils'
import { registerGokollabCpStream, resetGokollabCpStream } from '../utils/register-gokollab-dual-stream.utils'
import { resolveAppFlavor } from '../utils/page-hit'
import { getGtag } from '../utils/gtag-access'

type ProfileWithContact = { contactId?: string } | null | undefined

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const analyticsConfig = config.public.analytics
  const { logger } = useLogger()

  if (!analyticsConfig?.enabled) {
    logger.info('[Analytics] Disabled')
    return
  }

  analyticsService.initialize(analyticsConfig.debug || false)

  const appName = (config.public.appName as string) || ''
  const isGokollabApp = appName === APP_NAMES.GOKOLLAB

  // Current-user store drives the auth gate directly (source of truth), so authenticated analytics
  // is enabled the moment the profile has a contactId — not dependent on a navigation having run.
  let currentUserStore: ReturnType<typeof useCurrentUser>['store'] | undefined
  try {
    currentUserStore = useCurrentUser().store
  } catch (error) {
    logger.warn('[Analytics] Current user store unavailable', { error: error as Error })
  }

  const canSendAuthenticatedAnalytics = (): boolean =>
    computeAuthGate(
      isGokollabApp,
      (currentUserStore?.profile as ProfileWithContact)?.contactId,
      analyticsService.getContext().contactId as string | undefined
    )

  // Global (non-tenant) identity for the default resolver. Sourced from location + current-user
  // (never auth-layer) so the analytics layer does not import auth.store — that would cycle with
  // auth-layer, which already imports analytics types.
  const locationStore = useLocationStore()
  registerDefaultIdentitySource({
    getLocationId: () => locationStore.locationId,
    getContactId: () => (currentUserStore?.profile as ProfileWithContact)?.contactId,
  })

  const cleanupFunctions: (() => void)[] = []

  // ===== Register providers =====

  registerGoogleAnalyticsFromPlugin(
    {
      ...analyticsConfig,
      appEnv: config.public.appEnv as string,
      appName,
    },
    useGtag
  )

  if (analyticsConfig.providers?.backendAnalytics?.enabled) {
    analyticsService.registerProvider('backendAnalytics', new BackendAnalyticsProvider(analyticsConfig.debug))
  }

  // Pendo product page-hits: fired directly by the page-view pipeline (not registered) so they
  // stay login-independent — the auth gate below only applies to GA/backend/clicks/errors.
  const pendo = analyticsConfig.providers?.pendoAnalytics?.enabled
    ? new PendoAnalyticsProvider(resolveAppFlavor(config.public.appName))
    : null

  // ===== Auto-track: User Identification =====
  if (analyticsConfig.autoTrack?.userIdentification !== false && currentUserStore) {
    try {
      const { gtag } = getGtag()

      const stopUserWatch = watch(
        () => currentUserStore.profile,
        profile => {
          if (profile) {
            const contactId = (profile as { contactId?: string }).contactId

            if (!contactId) {
              if (!isGokollabApp) {
                logger.warn('[Analytics] Cannot identify: profile has no contactId')
              }
              return
            }

            const canSendToGa = isGoogleAnalyticsSendEnabled(config.public.appEnv as string)

            analyticsService.identify(contactId, {
              appName: config.public.appName || 'Unknown',
            })

            if (canSendToGa && appName === APP_NAMES.GOKOLLAB) {
              registerGokollabCpStream(gtag, getConfig().GOOGLE_ANALYTICS.GOKOLLAB_CP_STREAM, contactId, canSendToGa)
            }
          } else {
            analyticsService.updateContext('contactId', undefined)
            resetGokollabCpStream()
          }
        },
        { immediate: true }
      )

      cleanupFunctions.push(stopUserWatch)
    } catch (error) {
      logger.warn('[Analytics] User tracking not available', { error: error as Error })
    }
  }

  // ===== Auto-track: Clicks (via element id convention) =====
  if (analyticsConfig.autoTrack?.clicks !== false) {
    const handleClick = (event: MouseEvent) => {
      if (!canSendAuthenticatedAnalytics()) return
      const target = event.target as HTMLElement
      const clickTracking = getAutoTrackedClick(target)
      if (!clickTracking) return
      analyticsService.track(clickTracking.event, clickTracking.properties)
    }

    document.addEventListener('click', handleClick, { capture: true, passive: true })
    cleanupFunctions.push(() => document.removeEventListener('click', handleClick, { capture: true }))
  }

  // ===== Auto-track: Page Views & Route Context =====
  // Single pipeline for all providers — awaits the analytics context resolver per navigation
  // (tenant locationId on gokollab), then dispatches pendo (ungated) + GA/backend (gated) once.
  if (analyticsConfig.autoTrack?.pageViews !== false) {
    cleanupFunctions.push(startPageViewTracking({ gate: canSendAuthenticatedAnalytics, pendo }))
  }

  // ===== Auto-track: Errors =====
  if (analyticsConfig.autoTrack?.errors !== false) {
    const handleError = (event: ErrorEvent) => {
      if (!canSendAuthenticatedAnalytics()) return
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: event.message,
        errorType: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!canSendAuthenticatedAnalytics()) return
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: (event.reason as Error)?.message || String(event.reason),
        errorType: 'unhandled_promise_rejection',
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    cleanupFunctions.push(() => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    })
  }

  // ===== Cleanup =====
  let isCleanedUp = false

  const performCleanup = () => {
    if (isCleanedUp) return
    cleanupFunctions.forEach(fn => fn())
    isCleanedUp = true
  }

  // beforeunload paired with its removeEventListener in cleanup
  window.addEventListener('beforeunload', performCleanup)
  cleanupFunctions.push(() => window.removeEventListener('beforeunload', performCleanup))

  logger.info('[Analytics] Initialized successfully')
})
